import { FormEvent, useEffect, useState } from "react";
import { supabase } from "./lib/supabase";

type MediaSettings = {
  max_vehicle_photos: number;
  max_photo_bytes: number;
  watermark_enabled: boolean;
  watermark_text: string;
  watermark_position: string;
};

export function StandMediaSettings({ organizationId, canManage }: { organizationId: string; canManage: boolean }) {
  const [settings, setSettings] = useState<MediaSettings | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data, error } = await supabase
      .from("organization_media_settings")
      .select("max_vehicle_photos,max_photo_bytes,watermark_enabled,watermark_text,watermark_position")
      .eq("organization_id", organizationId)
      .single();
    setSettings(data as MediaSettings | null);
    if (error) setNotice(error.message);
  };

  useEffect(() => {
    void load();
  }, [organizationId]);

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setNotice("A guardar a configuração de fotografias…");
    setSaving(true);
    const form = new FormData(event.currentTarget);
    const { data: userData } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("organization_media_settings")
      .update({
        max_vehicle_photos: Number(form.get("max_vehicle_photos")),
        max_photo_bytes: Number(form.get("max_photo_mb")) * 1048576,
        watermark_enabled: form.get("watermark_enabled") === "on",
        watermark_text: String(form.get("watermark_text") ?? "").trim(),
        watermark_position: String(form.get("watermark_position") ?? "bottom_right"),
        updated_by: userData.user?.id,
        updated_at: new Date().toISOString(),
      })
      .eq("organization_id", organizationId);
    setSaving(false);
    setNotice(error?.message ?? "Configuração de fotografias guardada.");
    if (!error) await load();
  };

  if (!settings) return <div className="settings-future"><strong>A preparar fotografias e marca de água…</strong>{notice && <p>{notice}</p>}</div>;

  return (
    <form className="media-settings" onSubmit={save}>
      <div className="media-settings-intro">
        <strong>Fotografias das viaturas</strong>
        <p>Defina os limites dos carregamentos e escolha se as imagens publicadas no site recebem marca de água.</p>
      </div>
      {notice && <button type="button" className="stock-notice" role="status" aria-live="polite" disabled={saving} onClick={() => setNotice(null)}>{notice}{!saving && " ×"}</button>}
      <div className="media-settings-grid">
        <label><span>Máximo de fotografias por viatura</span><small className="field-help">Quantidade máxima que cada ficha de viatura pode guardar.</small><input name="max_vehicle_photos" type="number" min="1" max="100" defaultValue={settings.max_vehicle_photos} disabled={!canManage} /></label>
        <label><span>Tamanho máximo por fotografia (MB)</span><small className="field-help">Ficheiros maiores serão recusados antes do envio.</small><input name="max_photo_mb" type="number" min="1" max="50" defaultValue={Math.round(settings.max_photo_bytes / 1048576)} disabled={!canManage} /></label>
      </div>
      <div className="watermark-settings">
        <label className="simple-checkbox">
          <input name="watermark_enabled" type="checkbox" defaultChecked={settings.watermark_enabled} disabled={!canManage} />
          <span><strong>Aplicar marca de água</strong><small>A marca será aplicada apenas às novas versões públicas. O ficheiro original permanece privado e sem alterações.</small></span>
        </label>
        <div className="media-settings-grid">
          <label><span>Texto da marca de água</span><small className="field-help">Texto sobreposto às novas imagens públicas, normalmente o nome ou domínio do stand.</small><input name="watermark_text" defaultValue={settings.watermark_text} placeholder="Ex.: nome-do-stand.pt" disabled={!canManage} /></label>
          <label><span>Posição na fotografia</span><small className="field-help">Local onde a marca de água será aplicada nas imagens publicadas.</small><select name="watermark_position" defaultValue={settings.watermark_position} disabled={!canManage}><option value="bottom_right">Inferior direita</option><option value="bottom_left">Inferior esquerda</option><option value="top_right">Superior direita</option><option value="top_left">Superior esquerda</option><option value="center">Centro</option></select></label>
        </div>
      </div>
      {canManage && <button className="stock-primary" disabled={saving}>{saving ? "A guardar…" : "Guardar"}</button>}
    </form>
  );
}
